

import { useState, useMemo, useEffect, useRef } from "react";
import { AlertTriangle, Check } from "lucide-react";
import { mastersApi, vendorsApi, clientsApi, quotationSettingsApi } from "@/lib/api";
import { toUnit, unitSuffix, lineQuantity, packagePrice } from "@/lib/pricing";
import { useSettings } from "@/context/SettingsContext";
import AddServiceModal from "./AddServiceModal";
import AddVendorModal from "./AddVendorModal";
import BulkAddServicesModal from "./BulkAddServicesModal";
import AddClientModal from "./AddClientModal";
import VenueModal from "./VenueModal";
import DatePickerWithAvailability from "../components/DatePickerWithAvailability";
import QuotationBasicInfo from "./QuotationBasicInfo";
import QuotationServicesTable from "./QuotationServicesTable";
import QuotationVendorsTable from "./QuotationVendorsTable";
import QuotationFinancials from "./QuotationFinancials";
import QuotationAdditionalDetails from "./QuotationAdditionalDetails";

interface Service {
  id: number;
  name: string;
  selling_price: number;
  base_price?: number;
  cost_price?: number;
  vendor_id?: number;
  vendor?: { id: number; name: string };
  unit?: string;
  category?: string | null;
  is_active?: boolean;
}

interface Vendor {
  id: number;
  name: string;
  commission_type?: string;
  commission_value?: number;
}

/** One service line of a package, as GET /masters/packages returns it */
interface PackageLine {
  line_id?: number;
  id?: number;
  service_id: number;
  name: string;
  category?: string | null;
  description?: string | null;
  unit?: string;
  quantity?: number;
  unit_price: number;
  cost_price: number;
  vendor_id?: number | null;
  vendor_name?: string | null;
  is_optional?: boolean;
  service_active?: boolean | null;
  notes?: string | null;
}

interface Package {
  id: number;
  name: string;
  description?: string | null;
  is_active?: boolean;
  base_price: number;
  offered_price?: number;
  inclusions?: string[];
  min_guests?: number | null;
  max_guests?: number | null;
  event_types?: string[];
  discount_type?: "percent" | "fixed" | null;
  discount_value?: number;
  pricing?: { base_guests?: number };
  services_data?: PackageLine[];
  service_ids?: number[];
  vendors_data?: VendorItem[];
}

interface QuotationItem {
  id?: number;
  service_id?: number;
  description?: string;
  vendor_id?: number;
  quantity: number;
  unit_price: number;
  cost_price?: number;
  notes?: string;
  // ── Local only: never sent (the save payload in page.tsx picks its fields explicitly) ──
  /** Quantity follows the guest count: quantity = qty_per_guest × guests. false = the user took it over. */
  per_guest?: boolean;
  qty_per_guest?: number;
  /** Id of the package this line came from while it is still untouched */
  from_pkg?: number;
}

interface VendorItem {
  id?: number;
  vendor_id?: number;
  description?: string;
  quantity: number;
  unit?: string;
  unit_price: number;
  cost_price?: number;
  commission_percent?: number;
  commission_type?: string;
  notes?: string;
}

interface Client {
  id: number;
  name: string;
  email?: string;
  phone?: string;
}

interface Venue {
  id: number;
  name: string;
  location?: string;
  capacity?: number;
  price_per_plate?: number;
}

interface QuotationFormData {
  id?: number;
  quotation_number?: string;
  client_id: number;
  client_name: string;
  event_name: string;
  event_type: string;
  guests: string;
  event_date: string;
  package_id?: number;
  venue: string;
  venue_id?: number;
  services_data: QuotationItem[];
  vendors_data: VendorItem[];
  discount_type: 'fixed' | 'percentage';
  discount_value: string;
  tax: string;
  is_taxable: boolean;
  notes: string;
  valid_until: string;
  status: string;
  subtotal?: number;
  total_amount?: number;
}

interface FullScreenQuotationModalProps {
  isOpen: boolean;
  initialData?: Partial<QuotationFormData>;
  onSubmit: (data: QuotationFormData) => void;
  onCancel: () => void;
}

const venues = ["Grand Ballroom", "Crystal Hall", "Garden Terrace", "Executive Suite A", "Executive Suite B"];

const inr = (n: number | string | undefined) => "₹" + Math.round(Number(n) || 0).toLocaleString("en-IN");

const guestRange = (min?: number | null, max?: number | null) =>
  min && max ? `${min}–${max}` : min ? `${min}+` : `up to ${max}`;

/** A quotation line from a package line — keeps the line's vendor; per-guest lines follow the guest count */
const lineFromPackage = (line: PackageLine, guests: number, pkgId?: number): QuotationItem => {
  const perGuest = toUnit(line.unit) === "guest";
  return {
    service_id: line.service_id || line.id,
    description: line.name || line.description || "",
    vendor_id: line.vendor_id ?? undefined,
    quantity: lineQuantity(line, guests),
    unit_price: Number(line.unit_price) || 0,
    cost_price: Number(line.cost_price) || 0,
    notes: line.notes || "",
    per_guest: perGuest,
    qty_per_guest: perGuest ? Math.max(1, Number(line.quantity) || 1) : undefined,
    from_pkg: pkgId,
  };
};

export default function FullScreenQuotationModal({
  isOpen,
  initialData,
  onSubmit,
  onCancel,
}: FullScreenQuotationModalProps) {
  const { settings } = useSettings();
  const [packages, setPackages] = useState<Package[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [venuesList, setVenuesList] = useState<Venue[]>([]);
  const [eventTypeOptions, setEventTypeOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  // Resolved tax from settings — used both as initial value and in the sync effect
  const resolvedDefaultTax = (): string => {
    const v = settings?.general?.taxRate;
    // settings context has loaded a real value (may be "0", "5", "18", etc.)
    if (v !== undefined && v !== null) return String(v);
    return '0';
  };

  // Calculate default expiry date based on settings
  const getDefaultExpiryDate = (): string => {
    let validityDays = parseInt(settings?.general?.quotationValidityDays ?? '') || 0;
    if (!validityDays) {
      try {
        const raw = typeof window !== 'undefined' ? localStorage.getItem('settings') : null;
        if (raw) {
          const parsed = JSON.parse(raw);
          validityDays = parseInt(parsed?.general?.quotationValidityDays) || 7;
        }
      } catch { /* ignore */ }
    }
    if (!validityDays) validityDays = 7;
    const date = new Date();
    date.setDate(date.getDate() + validityDays);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const [formData, setFormData] = useState<QuotationFormData>({
    id: initialData?.id,
    quotation_number: initialData?.quotation_number,
    client_id: initialData?.client_id || 0,
    client_name: initialData?.client_name || "",
    event_name: initialData?.event_name || "",
    event_type: initialData?.event_type || "",
    guests: initialData?.guests || "",
    event_date: initialData?.event_date || "",
    package_id: initialData?.package_id,
    venue: initialData?.venue || "",
    venue_id: initialData?.venue_id,
    services_data: initialData?.services_data || [],
    vendors_data: initialData?.vendors_data || [],
    discount_type: initialData?.discount_type || "fixed",
    discount_value: initialData?.discount_value || "0",
    tax: initialData?.tax !== undefined ? String(initialData.tax) : resolvedDefaultTax(),
    is_taxable: initialData?.is_taxable !== undefined ? initialData.is_taxable : true,
    notes: initialData?.notes || "",
    valid_until: initialData?.valid_until || getDefaultExpiryDate(),
    status: initialData?.status || "draft",
    subtotal: initialData?.subtotal || 0,
    total_amount: initialData?.total_amount || 0,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof QuotationFormData, string>>>({});
  const [customOptions, setCustomOptions] = useState({
    venues: [...venues],
  });

  const [isAddServiceModalOpen, setIsAddServiceModalOpen] = useState(false);
  const [isAddVendorModalOpen, setIsAddVendorModalOpen] = useState(false);
  const [isBulkAddModalOpen, setIsBulkAddModalOpen] = useState(false);
  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);
  const [isAddVenueModalOpen, setIsAddVenueModalOpen] = useState(false);
  const [isCloseConfirmOpen, setIsCloseConfirmOpen] = useState(false);
  const [editingServiceIndex, setEditingServiceIndex] = useState<number | null>(null);
  const [editingVendorIndex, setEditingVendorIndex] = useState<number | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  // A package picked while the form already has the user's own lines — waits for "Replace / Add below / Cancel"
  const [pendingPackage, setPendingPackage] = useState<Package | null>(null);
  // Discount copied from a package; a later package may replace it as long as the user hasn't changed it
  const autoDiscount = useRef<{ type: string; value: string } | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setPendingPackage(null);
    autoDiscount.current = null;
  }, [isOpen, initialData]);

  // Pickers only get active packages; an existing quotation keeps showing its package even if it was archived
  useEffect(() => {
    const pid = initialData?.package_id;
    if (!isOpen || loading || !pid || packages.some(p => p.id === pid)) return;
    let cancelled = false;
    mastersApi.packages.list()
      .then(res => {
        const own = ((res.data?.data || res.data || []) as Package[]).find(p => p.id === pid);
        if (!cancelled && own) setPackages(prev => (prev.some(p => p.id === pid) ? prev : [...prev, own]));
      })
      .catch(() => { /* non-critical: the picker just shows no selection */ });
    return () => { cancelled = true; };
  }, [isOpen, loading, initialData?.package_id, packages]);

  // When settings finish loading, push the real taxRate into new-quotation forms.
  // "New" = no initialData.id (create mode), and the tax still matches the stale default.
  useEffect(() => {
    if (!isOpen) return;
    if (initialData?.id) return; // editing an existing quotation — don't overwrite
    const realTax = resolvedDefaultTax();
    setFormData(prev => ({ ...prev, tax: realTax }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, isOpen]);

  useEffect(() => {
    if (initialData && isOpen) {
      setFormData({
        id: initialData.id,
        quotation_number: initialData.quotation_number,
        client_id: initialData.client_id || 0,
        client_name: initialData.client_name || "",
        event_name: initialData.event_name || "",
        event_type: initialData.event_type || "",
        guests: initialData.guests || "",
        event_date: initialData.event_date || "",
        package_id: initialData.package_id,
        venue: initialData.venue || "",
        venue_id: initialData.venue_id,
        services_data: initialData.services_data || [],
        vendors_data: initialData.vendors_data || [],
        discount_type: initialData.discount_type || "fixed",
        discount_value: initialData.discount_value || "0",
        tax: initialData.tax !== undefined ? String(initialData.tax) : resolvedDefaultTax(),
        is_taxable: initialData.is_taxable !== undefined ? initialData.is_taxable : true,
        notes: initialData.notes || "",
        valid_until: initialData.valid_until || getDefaultExpiryDate(),
        status: initialData.status || "draft",
        subtotal: initialData.subtotal || 0,
        total_amount: initialData.total_amount || 0,
      });
      setHasChanges(false);
      setErrors({});
    }
  }, [initialData, isOpen]);

  const generateQuotationNumber = (quotations: any[]) => {
    if (!quotations || quotations.length === 0) {
      return "QT-001";
    }
    
    // Extract numbers from quotation_number field
    const numbers = quotations
      .map(q => {
        const match = q.quotation_number?.match(/\d+/);
        return match ? parseInt(match[0]) : 0;
      })
      .filter(n => n > 0)
      .sort((a, b) => b - a);
    
    const lastNumber = numbers[0] || 0;
    const nextNumber = lastNumber + 1;
    return `QT-${String(nextNumber).padStart(3, "0")}`;
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [packagesRes, servicesRes, vendorsRes, clientsRes, venuesRes, eventTypesRes, settingsRes] = await Promise.all([
        mastersApi.packages.list({ active_only: 1 }),
        mastersApi.services.list({ active_only: 1 }),
        vendorsApi.list('for_select=1'),
        clientsApi.list(),
        mastersApi.venues.list(),
        mastersApi.eventTypes.list({ status: 'active' }),
        quotationSettingsApi.get(),
      ]);
      // Archived packages are left out; the effect above adds back the one an existing quotation uses
      setPackages(packagesRes.data?.data || packagesRes.data || []);
      setServices(servicesRes.data?.data || servicesRes.data || []);
      setVendors(vendorsRes.data?.data || vendorsRes.data || []);
      setClients(clientsRes.data?.data || clientsRes.data || []);
      setVenuesList(venuesRes.data?.data || venuesRes.data || []);
      const etData = eventTypesRes.data?.data || eventTypesRes.data || [];
      setEventTypeOptions(etData.map((t: any) => t.name));
      
      // Auto-generate quotation number if creating new quotation using settings
      if (!initialData?.id) {
        const settings = settingsRes.data?.data || settingsRes.data;
        const preview = settings?.preview || settings?.next_number;
        if (preview) {
          setFormData(prev => ({ ...prev, quotation_number: preview }));
        }
      }
    } catch (error) {
      console.error("Failed to load data", error);
      setEventTypeOptions(["Wedding", "Corporate", "Birthday", "Social", "Festival"]);
    } finally {
      setLoading(false);
    }
  };

  const calculatedValues = useMemo(() => {
    const servicesTotal = formData.services_data.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const vendorsTotal = formData.vendors_data.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const subtotal = servicesTotal + vendorsTotal;

    const discountValue = parseFloat(formData.discount_value) || 0;
    const discount = formData.discount_type === 'percentage'
      ? subtotal * (discountValue / 100)
      : discountValue;
    const afterDiscount = Math.max(0, subtotal - discount);

    const taxPercent = formData.is_taxable ? (parseFloat(formData.tax) || 0) : 0;
    const taxAmount = afterDiscount * (taxPercent / 100);
    const total = afterDiscount + taxAmount;

    return { subtotal, discount, taxAmount, total, servicesTotal, vendorsTotal };
  }, [formData.services_data, formData.vendors_data, formData.discount_type, formData.discount_value, formData.tax, formData.is_taxable]);

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof QuotationFormData, string>> = {};
    
    if (!formData.client_id || formData.client_id === 0) newErrors.client_id = "Client is required";
    if (!formData.client_name.trim()) newErrors.client_name = "Client name is required";
    if (!formData.event_name.trim()) newErrors.event_name = "Event name is required";
    if (!formData.event_type) newErrors.event_type = "Event type is required";
    if (!formData.guests || parseInt(formData.guests) <= 0) newErrors.guests = "Valid guest count is required";
    if (!formData.event_date) newErrors.event_date = "Event date is required";
    if (formData.services_data.length === 0 && formData.vendors_data.length === 0) {
      newErrors.services_data = "At least one service or vendor is required";
    }
    if (!formData.valid_until) newErrors.valid_until = "Valid until date is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  // ── Per-guest lines ─────────────────────────────────────────────────────
  // Lines added from a package carry per_guest/qty_per_guest. Lines of a saved quotation come back
  // without them, so a per-guest service whose quantity is a whole multiple of the guest count
  // is treated as per guest too.

  const unitOf = (serviceId?: number): string | undefined => {
    if (!serviceId) return undefined;
    const svc = services.find(s => s.id === serviceId);
    if (svc?.unit) return svc.unit;
    for (const p of packages) {
      const line = p.services_data?.find(l => l.service_id === serviceId);
      if (line?.unit) return line.unit;
    }
    return undefined;
  };

  /** How many per guest this line takes (0 = not a per-guest line) */
  const perGuestQty = (line: QuotationItem, guests: number): number => {
    if (line.per_guest === false) return 0;
    if (line.per_guest && line.qty_per_guest) return line.qty_per_guest;
    if (guests > 0 && line.quantity > 0 && line.quantity % guests === 0 && toUnit(unitOf(line.service_id)) === "guest") {
      return line.quantity / guests;
    }
    return 0;
  };

  /** Re-size per-guest lines for a new guest count (other lines are left alone) */
  const followGuests = (lines: QuotationItem[], oldGuests: number, newGuests: number): QuotationItem[] =>
    lines.map(line => {
      const perGuest = perGuestQty(line, oldGuests);
      if (!perGuest) return line;
      return { ...line, per_guest: true, qty_per_guest: perGuest, quantity: newGuests > 0 ? perGuest * newGuests : line.quantity };
    });

  const handleChange = (field: keyof QuotationFormData, value: any) => {
    let updatedData = { [field]: value };

    // Auto-generate event_name from client name and event_type if event_name is empty
    if ((field === "event_type" || field === "client_name") && !formData.event_name.trim()) {
      const eventType = field === "event_type" ? value : formData.event_type;
      const clientName = field === "client_name" ? value : formData.client_name;

      if (eventType && clientName) {
        updatedData.event_name = `${clientName} - ${eventType}`;
      }
    }

    setFormData(prev => {
      const next = { ...prev, ...updatedData };
      if (field === "guests") {
        next.services_data = followGuests(prev.services_data, parseInt(prev.guests) || 0, parseInt(value) || 0);
      }
      return next;
    });
    setHasChanges(true);
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleCloseWithConfirmation = () => {
    if (hasChanges) {
      setIsCloseConfirmOpen(true);
    } else {
      onCancel();
    }
  };

  const handleConfirmClose = () => {
    setIsCloseConfirmOpen(false);
    onCancel();
  };

  const handleAddService = (newService: QuotationItem) => {
    if (editingServiceIndex !== null) {
      const updated = [...formData.services_data];
      const old = updated[editingServiceIndex];
      // Still follows the guest count unless the quantity was changed by hand
      const sameQty = old && newService.quantity === old.quantity;
      updated[editingServiceIndex] = {
        ...newService,
        per_guest: sameQty ? old.per_guest : false,
        qty_per_guest: sameQty ? old.qty_per_guest : undefined,
        from_pkg: undefined,
      };
      setFormData(prev => ({ ...prev, services_data: updated }));
      setEditingServiceIndex(null);
    } else {
      setFormData(prev => ({ ...prev, services_data: [...prev.services_data, newService] }));
    }
    setIsAddServiceModalOpen(false);
  };

  const handleBulkAddServices = (newServices: QuotationItem[]) => {
    setFormData(prev => ({ ...prev, services_data: [...prev.services_data, ...newServices] }));
    setIsBulkAddModalOpen(false);
  };

  const handleRemoveService = (index: number) => {
    setFormData(prev => ({
      ...prev,
      services_data: prev.services_data.filter((_, i) => i !== index),
    }));
  };

  const handleEditService = (index: number) => {
    setEditingServiceIndex(index);
    setIsAddServiceModalOpen(true);
  };

  const handleUpdateService = (index: number, field: keyof QuotationItem, value: any) => {
    setFormData(prev => {
      const updatedServices = [...prev.services_data];
      updatedServices[index] = {
        ...updatedServices[index],
        [field]: value,
        from_pkg: undefined, // edited → the user's line now
        // A hand-typed quantity stops following the guest count
        ...(field === "quantity" ? { per_guest: false, qty_per_guest: undefined } : {}),
      };
      return { ...prev, services_data: updatedServices };
    });
  };

  const handleAddVendor = (newVendor: VendorItem) => {
    if (editingVendorIndex !== null) {
      const updated = [...formData.vendors_data];
      updated[editingVendorIndex] = newVendor;
      setFormData(prev => ({ ...prev, vendors_data: updated }));
      setEditingVendorIndex(null);
    } else {
      setFormData(prev => ({ ...prev, vendors_data: [...prev.vendors_data, newVendor] }));
    }
    setIsAddVendorModalOpen(false);
  };

  const handleVendorCreated = async () => {
    try {
      const vendorsRes = await vendorsApi.list();
      setVendors(vendorsRes.data?.data || vendorsRes.data || []);
    } catch {
      // non-critical
    }
  };

  const handleRemoveVendor = (index: number) => {
    setFormData(prev => ({
      ...prev,
      vendors_data: prev.vendors_data.filter((_, i) => i !== index),
    }));
  };

  const handleClientCreated = (clientId: number, clientName: string) => {
    setFormData(prev => ({
      ...prev,
      client_id: clientId,
      client_name: clientName,
    }));
    setClients(prev => [...prev, { id: clientId, name: clientName }]);
    setIsAddClientModalOpen(false);
  };

  const handleVenueCreated = (venueId: number, venueName: string) => {
    setFormData(prev => ({
      ...prev,
      venue: venueName,
      venue_id: venueId,
    }));
    setVenuesList(prev => [...prev, { id: venueId, name: venueName }]);
    setIsAddVenueModalOpen(false);
    setHasChanges(true);
  };

  const handleEditVendor = (index: number) => {
    setEditingVendorIndex(index);
    setIsAddVendorModalOpen(true);
  };

  const handleUpdateVendor = (index: number, field: keyof VendorItem, value: any) => {
    setFormData(prev => {
      const updatedVendors = [...prev.vendors_data];
      updatedVendors[index] = { ...updatedVendors[index], [field]: value };
      return { ...prev, vendors_data: updatedVendors };
    });
  };

  const handleAddNew = (field: "venues", value: string) => {
    setCustomOptions(prev => ({
      ...prev,
      [field]: [...prev[field], value],
    }));
    const formField = "venue";
    handleChange(formField as keyof QuotationFormData, value);
  };

  const handleAddNewEventType = (value: string) => {
    setEventTypeOptions(prev => [...prev, value]);
    handleChange("event_type", value);
  };

  // ── Packages ────────────────────────────────────────────────────────────

  /** The lines a package puts on the quotation (optional add-ons are offered as chips instead) */
  const packageLines = (pkg: Package, guests: number): QuotationItem[] => {
    if (pkg.services_data && pkg.services_data.length > 0) {
      return pkg.services_data.filter(l => !l.is_optional).map(l => lineFromPackage(l, guests, pkg.id));
    }
    // Packages saved before service lines existed only list service ids
    return (pkg.service_ids || []).flatMap(svcId => {
      const service = services.find(s => s.id === svcId);
      return service
        ? [{
            service_id: service.id,
            description: service.name,
            vendor_id: service.vendor_id,
            quantity: 1,
            unit_price: Number(service.selling_price) || 0,
            cost_price: Number(service.base_price) || 0,
            notes: "",
            from_pkg: pkg.id,
          }]
        : [];
    });
  };

  const applyPackage = (pkg: Package, mode: "replace" | "append") => {
    setPendingPackage(null);
    const lines = packageLines(pkg, parseInt(formData.guests) || 0);

    // The package discount fills the quotation discount only while that is empty
    // (or still the one we copied from a previously picked package)
    const current = parseFloat(formData.discount_value) || 0;
    const auto = autoDiscount.current;
    const ours = current === 0 || (!!auto && auto.type === formData.discount_type && auto.value === formData.discount_value);
    let discount: Partial<QuotationFormData> = {};
    if (ours && pkg.discount_type && Number(pkg.discount_value) > 0) {
      const type = pkg.discount_type === "percent" ? "percentage" : "fixed";
      const value = String(Number(pkg.discount_value));
      autoDiscount.current = { type, value };
      discount = { discount_type: type, discount_value: value };
    } else if (ours && auto) {
      autoDiscount.current = null;
      discount = { discount_value: "0" };
    }

    setFormData(prev => ({
      ...prev,
      package_id: pkg.id,
      // A package without lines only sets the package; the lines stay as they are
      services_data: lines.length === 0 ? prev.services_data : mode === "append" ? [...prev.services_data, ...lines] : lines,
      ...discount,
    }));
    setHasChanges(true);
    if (lines.length) setErrors(prev => ({ ...prev, services_data: undefined }));
  };

  const handlePackageChange = (packageId: string) => {
    const pkg = packages.find(p => String(p.id) === packageId);
    if (!pkg) {
      setPendingPackage(null);
      handleChange("package_id", packageId ? parseInt(packageId) : undefined);
      return;
    }
    if (pkg.id === formData.package_id) return;
    // Lines the user added or edited are never dropped without asking
    const hasOwnLines = formData.services_data.some(l => !l.from_pkg);
    if (hasOwnLines && packageLines(pkg, 0).length > 0) {
      setPendingPackage(pkg);
      return;
    }
    applyPackage(pkg, "replace");
  };

  const addPackageAddOn = (line: PackageLine) => {
    // Picked by the user, so it counts as their line (no from_pkg)
    const item = { ...lineFromPackage(line, parseInt(formData.guests) || 0), from_pkg: undefined };
    setFormData(prev => ({ ...prev, services_data: [...prev.services_data, item] }));
    setHasChanges(true);
    setErrors(prev => ({ ...prev, services_data: undefined }));
  };

  const guestsNum = parseInt(formData.guests) || 0;
  const selectedPackage = packages.find(p => p.id === formData.package_id);

  // Lines as the table shows them: per-guest ones are tagged
  const tableLines = formData.services_data.map(line => ({ ...line, per_guest: perGuestQty(line, guestsNum) > 0 }));

  const addOns = (selectedPackage?.services_data || []).filter(
    l => l.is_optional && !formData.services_data.some(s => s.service_id === l.service_id)
  );

  const packageHints: string[] = [];
  if (selectedPackage) {
    const { name, min_guests: min, max_guests: max, event_types: types } = selectedPackage;
    if (guestsNum > 0 && ((min && guestsNum < min) || (max && guestsNum > max))) {
      packageHints.push(`${name} is for ${guestRange(min, max)} guests`);
    }
    if (types?.length && formData.event_type && !types.some(t => t.toLowerCase() === formData.event_type.toLowerCase())) {
      packageHints.push(`${name} is meant for ${types.join(" / ")} events`);
    }
  }

  const packageSummary = selectedPackage?.services_data?.length && guestsNum > 0
    ? packagePrice(selectedPackage.services_data, guestsNum, selectedPackage.discount_type, selectedPackage.discount_value)
    : null;

  const packagePanel = pendingPackage ? (
    <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 sm:p-4">
      <p className="text-sm font-semibold text-stone-800">Use “{pendingPackage.name}”?</p>
      <p className="text-xs text-stone-600 mt-0.5">
        This quotation already has {formData.services_data.length} service line{formData.services_data.length === 1 ? "" : "s"}.
        Replace them with the package lines, or keep them and add the package below?
      </p>
      <div className="flex flex-wrap gap-2 mt-3">
        <button
          type="button"
          onClick={() => applyPackage(pendingPackage, "replace")}
          className="flex-1 min-w-[140px] min-h-[40px] px-3 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 transition-colors"
        >
          Replace lines
        </button>
        <button
          type="button"
          onClick={() => applyPackage(pendingPackage, "append")}
          className="flex-1 min-w-[140px] min-h-[40px] px-3 py-2 rounded-lg bg-white border border-amber-300 text-amber-800 text-sm font-semibold hover:bg-amber-100 transition-colors"
        >
          Add package lines below mine
        </button>
        <button
          type="button"
          onClick={() => setPendingPackage(null)}
          className="flex-1 min-w-[100px] min-h-[40px] px-3 py-2 rounded-lg bg-white border border-stone-200 text-stone-600 text-sm font-semibold hover:bg-stone-100 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  ) : selectedPackage && (packageHints.length > 0 || selectedPackage.inclusions?.length || packageSummary) ? (
    <div className="space-y-2">
      {packageHints.map(hint => (
        <p key={hint} className="flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">
          <AlertTriangle className="w-3.5 h-3.5 mt-px flex-shrink-0" />
          {hint}
        </p>
      ))}
      {packageSummary && (
        <p className="text-xs text-stone-500">
          Package price for {guestsNum} guests:{" "}
          <span className="font-semibold text-stone-700">{inr(packageSummary.price)}</span>
          {packageSummary.discount > 0 && <> (after {inr(packageSummary.discount)} off)</>}
          {addOns.length > 0 && <>, add-ons extra</>}
        </p>
      )}
      {!!selectedPackage.inclusions?.length && (
        <div className="rounded-xl border border-stone-200 bg-stone-50 px-3 py-2.5">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-500 mb-1.5">Included</p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
            {selectedPackage.inclusions.map((item, i) => (
              <li key={i} className="flex items-start gap-1.5 text-xs text-stone-700">
                <Check className="w-3.5 h-3.5 mt-px text-emerald-600 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  ) : null;

  const addOnChips = selectedPackage && addOns.length > 0 ? (
    <div className="mt-3 pt-3 border-t border-stone-100">
      <p className="text-xs font-medium text-stone-500 mb-2">Add-ons in {selectedPackage.name}</p>
      <div className="flex flex-wrap gap-2">
        {addOns.map(line => (
          <button
            key={line.line_id ?? line.service_id}
            type="button"
            onClick={() => addPackageAddOn(line)}
            className="min-h-[40px] px-3 py-2 rounded-full border border-dashed border-amber-300 bg-amber-50/60 text-amber-800 text-xs sm:text-sm font-medium hover:bg-amber-100 transition-colors"
          >
            + {line.name} · {inr(line.unit_price)}{unitSuffix(line.unit) ? ` ${unitSuffix(line.unit)}` : ""}
          </button>
        ))}
      </div>
    </div>
  ) : null;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-stone-200 bg-gradient-to-r from-stone-50 to-white flex-shrink-0">
        <h2 className="text-lg sm:text-xl font-bold text-stone-800">
          {/* Pre-filled new quotations (from an enquiry, or a copy) have data but no id */}
          {initialData?.id ? "Edit Quotation" : "Create New Quotation"}
        </h2>
        <button 
          onClick={handleCloseWithConfirmation}
          className="w-8 h-8 rounded-lg bg-stone-100 text-stone-500 hover:bg-stone-200 hover:text-stone-700 flex items-center justify-center transition-colors flex-shrink-0"
          title="Close (Ctrl+W)"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

        {/* Content - Scrollable */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="space-y-4 sm:space-y-6 mx-auto">
            {/* Basic Info */}
            <QuotationBasicInfo
              quotationNumber={formData.quotation_number}
              clientId={formData.client_id}
              clientName={formData.client_name}
              eventName={formData.event_name}
              eventType={formData.event_type}
              guests={formData.guests}
              eventDate={formData.event_date}
              venue={formData.venue}
              venueId={formData.venue_id}
              packageId={formData.package_id}
              clients={clients}
              packages={packages}
              venues={venuesList}
              loading={loading}
              errors={errors}
              customOptions={customOptions}
              eventTypeOptions={eventTypeOptions}
              onQuotationNumberChange={(v) => handleChange("quotation_number", v)}
              onClientIdChange={(v) => handleChange("client_id", v)}
              onClientNameChange={(v) => handleChange("client_name", v)}
              onEventNameChange={(v) => handleChange("event_name", v)}
              onEventTypeChange={(v) => handleChange("event_type", v)}
              onGuestsChange={(v) => handleChange("guests", v)}
              onEventDateChange={(v) => handleChange("event_date", v)}
              onVenueChange={(v) => handleChange("venue", v)}
              onVenueIdChange={(v) => handleChange("venue_id", v)}
              onPackageChange={handlePackageChange}
              packagePanel={packagePanel}
              onAddNewEventType={(v) => handleAddNewEventType(v)}
              onAddNewVenue={() => setIsAddVenueModalOpen(true)}
              onAddNewClient={() => setIsAddClientModalOpen(true)}
            />

            {/* Services Table */}
            <QuotationServicesTable
              services={tableLines}
              vendors={vendors}
              onAddService={() => {
                setEditingServiceIndex(null);
                setIsAddServiceModalOpen(true);
              }}
              onEditService={handleEditService}
              onRemoveService={handleRemoveService}
              onUpdateService={handleUpdateService}
              onBulkAdd={() => setIsBulkAddModalOpen(true)}
              footer={addOnChips}
            />

            {/* Vendors Table */}
            <QuotationVendorsTable
              vendors={formData.vendors_data}
              vendorList={vendors}
              onAddVendor={() => {
                setEditingVendorIndex(null);
                setIsAddVendorModalOpen(true);
              }}
              onEditVendor={handleEditVendor}
              onRemoveVendor={handleRemoveVendor}
              onUpdateVendor={handleUpdateVendor}
            />

            {/* Financials */}
            <QuotationFinancials
              discountType={formData.discount_type}
              discountValue={formData.discount_value}
              tax={formData.tax}
              isTaxable={formData.is_taxable}
              validUntil={formData.valid_until}
              calculatedValues={calculatedValues}
              errors={errors}
              onDiscountTypeChange={(v) => handleChange("discount_type", v)}
              onDiscountValueChange={(v) => handleChange("discount_value", v)}
              onTaxChange={(v) => handleChange("tax", v)}
              onIsTaxableChange={(v) => handleChange("is_taxable", v)}
              onValidUntilChange={(v) => handleChange("valid_until", v)}
            />

            {/* Additional Details */}
            <QuotationAdditionalDetails
              status={formData.status}
              notes={formData.notes}
              onStatusChange={(v) => handleChange("status", v)}
              onNotesChange={(v) => handleChange("notes", v)}
            />
          </div>
        </form>

        {/* Footer */}
        <div className="border-t border-stone-200 bg-stone-50 px-4 sm:px-6 py-3 flex gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-4 py-2.5 rounded-lg bg-white text-stone-600 font-semibold text-sm border border-stone-200 hover:bg-stone-100 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="flex-1 px-4 py-2.5 rounded-lg text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity"
          >
            {initialData?.id ? "Update Quotation" : "Create Quotation"}
          </button>
        </div>

      {/* Close Confirmation Dialog */}
      {isCloseConfirmOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-stone-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm mx-4">
            <h3 className="text-lg font-bold text-stone-800 mb-2">Discard Changes?</h3>
            <p className="text-sm text-stone-600 mb-6">
              You have unsaved changes. Are you sure you want to close without saving?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setIsCloseConfirmOpen(false)}
                className="flex-1 px-4 py-2 rounded-lg border border-stone-200 text-stone-700 font-medium hover:bg-stone-50 transition-colors"
              >
                Keep Editing
              </button>
              <button
                onClick={handleConfirmClose}
                className="flex-1 px-4 py-2 rounded-lg bg-rose-600 text-white font-medium hover:bg-rose-700 transition-colors"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Service Modal */}
      <AddServiceModal
        isOpen={isAddServiceModalOpen}
        onClose={() => {
          setIsAddServiceModalOpen(false);
          setEditingServiceIndex(null);
        }}
        onAdd={handleAddService}
        services={services}
        vendors={vendors}
        guests={guestsNum}
        initialData={editingServiceIndex !== null ? formData.services_data[editingServiceIndex] : undefined}
      />

      {/* Bulk Add Services Modal */}
      <BulkAddServicesModal
        isOpen={isBulkAddModalOpen}
        onClose={() => setIsBulkAddModalOpen(false)}
        onAdd={handleBulkAddServices}
        services={services}
        vendors={vendors}
        guests={guestsNum}
      />

      {/* Add Vendor Modal */}
      <AddVendorModal
        isOpen={isAddVendorModalOpen}
        onClose={() => {
          setIsAddVendorModalOpen(false);
          setEditingVendorIndex(null);
        }}
        onAdd={handleAddVendor}
        vendors={vendors}
        initialData={editingVendorIndex !== null ? formData.vendors_data[editingVendorIndex] : undefined}
        onVendorCreated={handleVendorCreated}
      />

      {/* Add Client Modal */}
      <AddClientModal
        isOpen={isAddClientModalOpen}
        onClose={() => setIsAddClientModalOpen(false)}
        onClientCreated={handleClientCreated}
      />

      {/* Add Venue Modal */}
      <VenueModal
        isOpen={isAddVenueModalOpen}
        onClose={() => setIsAddVenueModalOpen(false)}
        onVenueCreated={handleVenueCreated}
      />
    </div>
  );
}
